import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Moon, Sun, Loader2, Mail, Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const LOGO_URL = "https://childhood.com.co/wp-content/uploads/2018/09/childhood_new_logo.png";

export default function Login() {
    const { user, loginAdmin, requestOtp, verifyOtp, loading } = useAuth();
    const { theme, toggle } = useTheme();
    const nav = useNavigate();

    const [mode, setMode] = useState("parent");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [otpStage, setOtpStage] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (user && !loading) {
            nav(user.role === "admin" ? "/admin" : "/parent", { replace: true });
        }
    }, [user, loading, nav]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/parent"} replace />;

    const handleAdminLogin = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await loginAdmin(username, password);
            toast.success("Welcome back!");
            nav("/admin", { replace: true });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Login failed");
        } finally {
            setSubmitting(false);
        }
    };

    const handleRequestOtp = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await requestOtp(email);
            toast.success("If registered, an OTP has been emailed to you.");
            setOtpStage(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to send OTP");
        } finally {
            setSubmitting(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await verifyOtp(email, otp);
            toast.success("Logged in successfully!");
            nav("/parent", { replace: true });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Invalid OTP");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-background text-foreground flex flex-col">
            <header className="flex items-center justify-between p-4 md:p-6">
                <div className="flex items-center gap-3">
                    <img src={LOGO_URL} alt="Childhood Cuddle and Care" className="h-12 w-auto" data-testid="login-logo" />
                    <div className="hidden sm:block">
                        <div className="font-bold font-heading text-lg leading-tight">Childhood</div>
                        <div className="text-xs text-muted-foreground -mt-0.5">Cuddle &amp; Care Portal</div>
                    </div>
                </div>
                <Button variant="ghost" size="icon" onClick={toggle} data-testid="login-theme-toggle" aria-label="Toggle theme">
                    {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </Button>
            </header>

            <div className="flex-1 grid lg:grid-cols-2 gap-8 px-4 md:px-12 pb-12">
                {/* Left hero */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="hidden lg:flex flex-col justify-center"
                >
                    <span className="inline-block w-fit text-xs uppercase tracking-wider bg-secondary text-secondary-foreground px-4 py-1.5 rounded-full font-semibold">
                        Parent Communication Portal
                    </span>
                    <h1 className="font-heading text-4xl xl:text-6xl font-black tracking-tight mt-6 leading-[1.05]">
                        Stay close to your{" "}
                        <span className="bg-primary text-primary-foreground px-3 py-0.5 rounded-2xl inline-block -rotate-1">
                            little one
                        </span>
                        ,<br /> wherever you are.
                    </h1>
                    <p className="mt-6 text-lg text-muted-foreground max-w-md leading-relaxed">
                        A warm, secure window into your child&apos;s day at Childhood Cuddle &amp; Care —
                        notifications, photos, and important documents, all in one place.
                    </p>
                    <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
                        <div className="bg-card border border-border rounded-2xl p-4">
                            <div className="text-2xl font-black font-heading text-secondary-foreground">64+</div>
                            <div className="text-xs text-muted-foreground mt-1">Years of care</div>
                        </div>
                        <div className="bg-card border border-border rounded-2xl p-4">
                            <div className="text-2xl font-black font-heading text-secondary-foreground">100%</div>
                            <div className="text-xs text-muted-foreground mt-1">Mobile-friendly</div>
                        </div>
                        <div className="bg-card border border-border rounded-2xl p-4">
                            <div className="text-2xl font-black font-heading text-secondary-foreground">24h</div>
                            <div className="text-xs text-muted-foreground mt-1">OTP validity</div>
                        </div>
                    </div>
                </motion.div>

                {/* Right form */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                    className="flex items-center justify-center"
                >
                    <Card className="w-full max-w-md rounded-3xl border-border shadow-sm">
                        <CardHeader>
                            <CardTitle className="font-heading text-2xl">Sign in</CardTitle>
                            <CardDescription>Choose how you&apos;d like to access the portal.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Tabs value={mode} onValueChange={(v) => { setMode(v); setOtpStage(false); }} className="w-full">
                                <TabsList className="grid grid-cols-2 w-full rounded-full h-11 mb-6">
                                    <TabsTrigger value="parent" className="rounded-full" data-testid="tab-parent-login">Parent</TabsTrigger>
                                    <TabsTrigger value="admin" className="rounded-full" data-testid="tab-admin-login">Administrator</TabsTrigger>
                                </TabsList>

                                <TabsContent value="parent">
                                    {!otpStage ? (
                                        <form onSubmit={handleRequestOtp} className="space-y-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="email">Email address</Label>
                                                <div className="relative">
                                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                                    <Input
                                                        id="email"
                                                        type="email"
                                                        required
                                                        autoComplete="email"
                                                        placeholder="parent@example.com"
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                        className="pl-10 h-12 rounded-xl"
                                                        data-testid="parent-email-input"
                                                    />
                                                </div>
                                            </div>
                                            <Button
                                                type="submit"
                                                disabled={submitting}
                                                className="w-full h-12 rounded-full font-bold"
                                                data-testid="parent-request-otp-btn"
                                            >
                                                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Send Login Code <ArrowRight className="h-4 w-4 ml-2" /></>)}
                                            </Button>
                                            <p className="text-xs text-muted-foreground text-center">
                                                We&apos;ll email you a 6-digit code valid for 24 hours.
                                            </p>
                                        </form>
                                    ) : (
                                        <form onSubmit={handleVerifyOtp} className="space-y-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="otp">Enter the 6-digit code sent to</Label>
                                                <div className="text-sm font-medium text-secondary-foreground">{email}</div>
                                                <Input
                                                    id="otp"
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength={6}
                                                    required
                                                    value={otp}
                                                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                                                    placeholder="123456"
                                                    className="h-14 text-center text-2xl tracking-[0.5em] font-bold rounded-xl"
                                                    data-testid="parent-otp-input"
                                                />
                                            </div>
                                            <Button
                                                type="submit"
                                                disabled={submitting || otp.length !== 6}
                                                className="w-full h-12 rounded-full font-bold"
                                                data-testid="parent-verify-otp-btn"
                                            >
                                                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Sign in"}
                                            </Button>
                                            <button
                                                type="button"
                                                onClick={() => { setOtpStage(false); setOtp(""); }}
                                                className="text-sm text-muted-foreground hover:text-foreground w-full"
                                                data-testid="parent-otp-change-email"
                                            >
                                                Use a different email
                                            </button>
                                        </form>
                                    )}
                                </TabsContent>

                                <TabsContent value="admin">
                                    <form onSubmit={handleAdminLogin} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="username">Username</Label>
                                            <Input
                                                id="username"
                                                type="text"
                                                required
                                                autoComplete="username"
                                                value={username}
                                                onChange={(e) => setUsername(e.target.value)}
                                                placeholder="administrator"
                                                className="h-12 rounded-xl"
                                                data-testid="admin-username-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="password">Password</Label>
                                            <div className="relative">
                                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                                <Input
                                                    id="password"
                                                    type="password"
                                                    required
                                                    autoComplete="current-password"
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    placeholder="••••••••"
                                                    className="pl-10 h-12 rounded-xl"
                                                    data-testid="admin-password-input"
                                                />
                                            </div>
                                        </div>
                                        <Button
                                            type="submit"
                                            disabled={submitting}
                                            className="w-full h-12 rounded-full font-bold"
                                            data-testid="admin-login-btn"
                                        >
                                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in as Administrator"}
                                        </Button>
                                    </form>
                                </TabsContent>
                            </Tabs>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>

            <footer className="text-center text-xs text-muted-foreground py-6 px-4">
                &copy; {new Date().getFullYear()} Star Childhood Cuddle and Care Pvt Ltd · SJR iPark, Whitefield, Bengaluru
            </footer>
        </div>
    );
}
